'use client';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { getBaseImageUrl } from '@/constants/hooks';
import { EventBannerResponse, PromotionBannerResponse, SubPromotionBanner } from '@/services/dataTypes';

interface Props {
    domain: string;
    banners: EventBannerResponse[] | PromotionBannerResponse[] | SubPromotionBanner[];
    eventName: string;
}

export default function EventBanner({ domain, banners, eventName }: Props) {
    const [hydrated, setHydrated] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const isMultiple = banners.length > 1;

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: true,
        slides: { perView: 1 },
        slideChanged(s) {
            setCurrentSlide(s.track.details.rel);
        },
    });

    useEffect(() => { setHydrated(true); }, []);

    useEffect(() => {
        if (!isMultiple) return;
        timerRef.current = setInterval(() => { slider.current?.next(); }, 4500);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [slider, isMultiple]);

    const pauseAuto = () => { if (timerRef.current) clearInterval(timerRef.current); };
    const resumeAuto = () => {
        if (!isMultiple) return;
        timerRef.current = setInterval(() => { slider.current?.next(); }, 4500);
    };

    return (
        <div
            className="relative overflow-hidden mx-2 md:mx-4 xl:mx-6 mt-2 md:mt-4 xl:mt-6 rounded-2xl"
            style={{
                border: '1px solid #E8F5EE',
                boxShadow: '0 4px 24px rgba(74,154,110,0.08)',
                opacity: hydrated ? 1 : 0,
                transition: 'opacity 0.3s ease',
            }}
            onMouseEnter={pauseAuto}
            onMouseLeave={resumeAuto}
        >
            {isMultiple ? (
                <>
                    <div ref={sliderRef} className="keen-slider">
                        {banners.map((slide, i) => (
                            <div key={i} className="keen-slider__slide">
                                <div className="relative w-full" style={{ aspectRatio: '1400/440' }}>
                                    <Image
                                        src={getBaseImageUrl(domain, (slide as any).banner, '')}
                                        alt={`${eventName} Banner ${i + 1}`}
                                        fill
                                        className="object-cover"
                                        priority={i === 0}
                                        loading={i === 0 ? 'eager' : 'lazy'}
                                        sizes="(max-width: 768px) 100vw, 1400px"
                                        unoptimized
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Prev / Next arrows */}
                    <button
                        type="button"
                        aria-label="Previous slide"
                        onClick={() => slider.current?.prev()}
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                        style={{ border: '1px solid #E8F5EE' }}
                    >
                        <svg className="w-4 h-4 text-[#5D27A1]" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        aria-label="Next slide"
                        onClick={() => slider.current?.next()}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                        style={{ border: '1px solid #E8F5EE' }}
                    >
                        <svg className="w-4 h-4 text-[#5D27A1]" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                    </button>

                    {/* Dot indicators */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
                        {banners.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Go to slide ${i + 1}`}
                                onClick={() => slider.current?.moveToIdx(i)}
                                className="rounded-full transition-all duration-300"
                                style={{
                                    width: i === currentSlide ? '20px' : '6px',
                                    height: '6px',
                                    background: i === currentSlide ? '#5D27A1' : 'rgba(255,255,255,0.6)',
                                }}
                            />
                        ))}
                    </div>
                </>
            ) : (
                /* Single banner — no slider overhead */
                <div className="relative w-full" style={{ aspectRatio: '1400/440' }}>
                    <Image
                        src={getBaseImageUrl(domain, (banners[0] as any).banner, '')}
                        alt={`${eventName} Banner`}
                        fill
                        className="object-cover"
                        priority
                        loading="eager"
                        sizes="(max-width: 768px) 100vw, 1400px"
                        unoptimized
                    />
                </div>
            )}
        </div>
    );
}
